import React from "react";
import { useTranslation } from "react-i18next";
import SupportForm from "./components/SupportForm";

function SupportPage() {
  const { t } = useTranslation("global");
  
  return (
    <>
      {/* Hero Section with 3D Tailwind Design */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-8">
            <div className="md:w-1/2 space-y-6">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-black">
                {t("support.title", "Support Technique")}
              </h1>
              <p className="text-lg text-gray-600 max-w-xl">
                {t("support.subtitle", "Notre équipe de support est là pour vous aider à résoudre tous vos problèmes techniques")}
              </p>
              <div className="w-20 h-1 bg-blue-600"></div>
            </div>
            
            <div className="md:w-1/2 flex justify-center">
              {/* 3D Support Design using Tailwind */}
              <div className="relative w-80 h-80 perspective-1000">
                {/* Background elements */}
                <div className="absolute w-64 h-64 bg-blue-100 rounded-full -top-10 -right-10 opacity-40"></div>
                <div className="absolute w-40 h-40 bg-indigo-100 rounded-full bottom-4 -left-8 opacity-50"></div>
                
                {/* 3D Support Elements */}
                <div className="absolute inset-0 flex items-center justify-center">
                  {/* Main support card */}
                  <div className="w-64 h-64 bg-white rounded-2xl shadow-xl relative overflow-hidden transform rotate-12 transition-transform duration-500 hover:rotate-0 group">
                    {/* Card header */}
                    <div className="absolute top-0 left-0 w-full h-16 bg-gradient-to-r from-blue-500 to-blue-600">
                      <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex space-x-1">
                          <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                          <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                          <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                        </div>
                        <div className="w-24 h-2 bg-white/30 rounded-full"></div>
                      </div>
                    </div>
                    
                    {/* Card content */}
                    <div className="absolute top-16 left-0 right-0 bottom-0 p-4 overflow-hidden">
                      <div className="w-full h-6 bg-gray-200 rounded-md mb-3"></div>
                      <div className="w-3/4 h-4 bg-gray-200 rounded-md mb-3"></div>
                      <div className="w-full h-4 bg-gray-200 rounded-md mb-3"></div>
                      <div className="w-5/6 h-4 bg-gray-200 rounded-md mb-3"></div>
                      
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="w-full h-10 bg-blue-500 rounded-md flex items-center justify-center">
                          <div className="w-20 h-2 bg-white rounded-full"></div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Card reflection */}
                    <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent opacity-0 group-hover:opacity-50 transition-opacity duration-500"></div>
                  </div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute top-8 -right-4 w-16 h-16 bg-white rounded-full shadow-lg flex items-center justify-center transform hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </div>
                
                <div className="absolute top-32 -left-4 w-14 h-14 bg-white rounded-full shadow-lg flex items-center justify-center transform hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                
                <div className="absolute bottom-12 right-0 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center transform hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                
                {/* Chat bubbles */}
                <div className="absolute bottom-24 left-6 bg-blue-100 rounded-lg p-2 shadow-md transform -rotate-6 hover:scale-105 transition-transform">
                  <div className="w-20 h-3 bg-blue-300 rounded-full mb-1"></div>
                  <div className="w-16 h-3 bg-blue-300 rounded-full"></div>
                </div>
                
                <div className="absolute top-36 right-10 bg-green-100 rounded-lg p-2 shadow-md transform rotate-3 hover:scale-105 transition-transform">
                  <div className="w-16 h-3 bg-green-300 rounded-full mb-1"></div>
                  <div className="w-12 h-3 bg-green-300 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Support Info Section */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center p-2 bg-blue-100 rounded-full mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">
              {t("support.howCanWeHelp", "Comment pouvons-nous vous aider ?")}
            </h2>
            <p className="text-gray-600 mb-0">
              {t("contactForm.formtext")}
            </p>
          </div>
        </div>
      </section>
      
      {/* Support Form Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <SupportForm />
          </div>
        </div>
      </section>
      
      {/* FAQ Quick Links Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">
              {t("support.quickLinks", "Ressources d'aide")}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t("support.quickLinksSubtitle", "Consultez nos réponses aux questions fréquentes")}
            </p>
            <div className="w-20 h-1 bg-blue-600 mx-auto mt-4"></div>
          </div>
          
          <div className="max-w-md mx-auto">
            <a href="faq" className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 text-center block">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-heading font-bold text-blue-900 mb-2">
                {t("FAQ.title")}
              </h3>
              <p className="text-gray-600">
                {t("support.faqText", "Consultez nos réponses aux questions fréquemment posées")}
              </p>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

export default SupportPage;